import type { PurchaseReturnResponse } from '@palaccount/contracts';
import { Money } from '@palaccount/money';

import type { TrackingPayload } from '@/features/sales/tracking-capture';

/**
 * PUR-04's TWO caps, mirrored for the debit-note editor.
 *
 * The sales return has one ceiling — billed minus already returned. A
 * purchase return has that one (BR-PUR-04-04) AND a second one the sales
 * side has no equivalent of (BR-PUR-04-05): **what is actually still on the
 * shelf**. The goods are physically going back to the supplier, so a return
 * of forty units when twelve remain is not a paperwork problem, it is a lorry
 * that cannot be loaded. §14's own two sentences are the two ceilings:
 *
 *   "The bill charged for 200 of ITM-3045 and 180 have already gone back.
 *    You can return at most 20 more."      ← billed − already returned
 *   "Only 12 of ITM-3045 are in Main Warehouse. You cannot send back 40."
 *                                          ← on hand
 *
 * The second has an escape the first does not: a VALUE-ONLY return (§15) can
 * be raised when the goods have been used or sold on, and it claims money
 * without moving stock — so the on-hand ceiling is lifted entirely when the
 * switch is on. That asymmetry is why this file exists rather than the sales
 * caps being reused wholesale.
 *
 * The server and the database posting gate enforce both regardless; this
 * mirror exists so the editor can refuse and explain BEFORE the round trip,
 * with the same numbers the server would use.
 */

/**
 * The ARITHMETIC TAG these helpers default to, and it REACHES NO COLUMN and
 * no label. Every `Money` below holds a QUANTITY — billed, returned, on the
 * shelf — and the tag exists only because `Money` refuses to compare across
 * tags. Callers pass the document's own code so the values they mix with it
 * agree; nothing here decides what money anything is in.
 */
const TAG = 'USD';

export interface ReturnableBillLine {
  billedQty: string;
  alreadyReturnedQty: string;
  /** What the shelf the goods came in to holds right now. */
  onHandQty: string;
}

/** billed − already returned, as a decimal string (never negative). */
export function remainingQty(line: ReturnableBillLine, currency = TAG): string {
  const left = Money.of(line.billedQty, currency).minus(Money.of(line.alreadyReturnedQty, currency));
  return left.isNegative() ? '0' : left.toDecimalString();
}

/**
 * The ceiling this row may actually be typed to: the lower of the two, or
 * the paperwork ceiling alone when the return is value-only.
 */
export function returnCeiling(
  line: ReturnableBillLine,
  { valueOnly = false, currency = TAG }: { valueOnly?: boolean; currency?: string } = {},
): string {
  const paper = Money.of(remainingQty(line, currency), currency);
  if (valueOnly) return paper.toDecimalString();
  const shelf = Money.of(line.onHandQty, currency);
  const floor = shelf.isNegative() ? Money.zero(currency) : shelf;
  return (floor.lessThan(paper) ? floor : paper).toDecimalString();
}

/**
 * Wave 1C — "not going back": a blank or ZERO quantity is a row the reader
 * left out, not a figure to check. The wire already treats it so
 * (`toReturnLines` drops it); the editor used to paint it red as an error
 * while the save quietly ignored it.
 */
export function isNotReturned(requested: string): boolean {
  const value = requested.trim();
  return value === '' || /^0+(\.0+)?$/.test(value);
}

/**
 * Whether `requested` fits under BOTH ceilings. A blank or zero row is "not
 * returned" and fits by definition (nothing to cap); an invalid or negative
 * figure never does.
 */
export function fitsCaps(
  requested: string,
  line: ReturnableBillLine,
  options: { valueOnly?: boolean; currency?: string } = {},
): boolean {
  if (isNotReturned(requested)) return true;
  const value = requested.trim();
  if (!/^\d+(\.\d+)?$/.test(value)) return false;
  const currency = options.currency ?? TAG;
  const qty = Money.of(value, currency);
  return !qty.greaterThan(Money.of(returnCeiling(line, options), currency));
}

/** True when the SHELF, not the bill, is what stops this row (ERR-PUR-04-03). */
export function shelfIsTheLimit(
  requested: string,
  line: ReturnableBillLine,
  currency = TAG,
): boolean {
  const value = requested.trim();
  if (!/^\d+(\.\d+)?$/.test(value)) return false;
  const qty = Money.of(value, currency);
  const shelf = Money.of(line.onHandQty, currency);
  return qty.greaterThan(shelf) && !qty.greaterThan(Money.of(remainingQty(line, currency), currency));
}

/**
 * The editor's returning set → the wire's lines array: only rows with a
 * positive quantity ride; zero/blank rows mean "not going back".
 *
 * `tracking` is whatever BR-MD-03-17 needs for that row — nothing for an
 * untracked item, the serials or the lot going back for a tracked one. There
 * are no component blocks here and there should not be: a goods receipt takes
 * a bundle in as ITSELF (PUR-02 never expands a kit), so the return sends
 * back exactly what arrived.
 */
export function toReturnLines<T extends TrackingPayload>(
  rows: readonly {
    billLineNo: number;
    qty: string;
    /** Wave 5D: the row's own warehouse; blank = where the goods came in. */
    warehouseId?: string | undefined;
    tracking?: T;
  }[],
): ({ billLineNo: number; qty: string; warehouseId?: string } & TrackingPayload)[] {
  return rows
    .map((row) => ({
      billLineNo: row.billLineNo,
      qty: row.qty.trim(),
      ...(row.warehouseId ? { warehouseId: row.warehouseId } : {}),
      ...(row.tracking ?? {}),
    }))
    .filter((row) => /^\d+(\.\d+)?$/.test(row.qty) && !/^0+(\.0+)?$/.test(row.qty));
}

/** '12.000' → '12', '4.500' → '4.5' — quantities read like the spec writes them. */
export function trimQty(value: string): string {
  return value.includes('.') ? value.replace(/\.?0+$/, '') : value;
}

/** A posted debit note is locked except its notes (R8 / BR-PUR-04-13). */
export function isPurchaseReturnEditable(status: PurchaseReturnResponse['status']): boolean {
  return status === 'draft';
}
